// eslint-disable-next-line no-restricted-imports
import { Button as MuiButton } from '@mui/material';
import { MouseEvent as ReactMouseEvent, useRef, useState } from 'react';
import { KeyboardArrowDown } from '@icons'; // update with your real icon
import Button, { circularProgressColor } from './Button';
import { ButtonGroup, CircularProgress, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from './mui-index';

export interface SplitButtonOption {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface SplitButtonConfig {
  options: SplitButtonOption[];
  defaultIndex?: number;
}

interface SplitButtonProps {
  config: SplitButtonConfig;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  loading?: boolean;
  disabledTooltipContent?: string;
}

export default function SplitButton({
  config,
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  loading = false,
  disabledTooltipContent,
}: SplitButtonProps) {
  const isSingleOption = config.options.length === 1;
  const onlyOption = isSingleOption ? config.options[0] : null;

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(config.defaultIndex ?? 0);

  const handleClick = () => {
    const selected = config.options[selectedIndex];
    if (!selected.disabled) {
      selected.onClick();
    }
  };

  const handleMenuItemClick = (_: ReactMouseEvent<HTMLLIElement>, index: number) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  if (isSingleOption) {
    return (
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        loading={loading}
        disabled={onlyOption?.disabled}
        disabledTooltipContent={disabledTooltipContent}
        onClick={onlyOption?.onClick}
      >
        {onlyOption?.label}
      </Button>
    );
  }

  const selected = config.options[selectedIndex];

  return (
    <>
      <ButtonGroup
        variant={variant}
        disableElevation
        ref={anchorRef}
        aria-label="Split button"
        fullWidth={fullWidth}
        size={size}
        color="primary"
      >
        <MuiButton
          startIcon={
            loading ? <CircularProgress size={12} sx={{ color: circularProgressColor[variant] }} /> : undefined
          }
          onClick={handleClick}
          disabled={selected?.disabled || loading}
          variant={variant}
          size={size}
          sx={{ whiteSpace: 'nowrap' }}
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select option"
          aria-haspopup="menu"
        >
          {selected?.label}
        </MuiButton>
        <MuiButton
          size={size}
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select option"
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{ px: 1, minWidth: 0 }}
          disabled={selected?.disabled || loading}
          variant={variant}
        >
          <KeyboardArrowDown />
        </MuiButton>
      </ButtonGroup>
      <Popper
        style={{ zIndex: 9999 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        className="cap-split-button-popper"
        transition
        disablePortal
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {config.options.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      disabled={option.disabled}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
